import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { AddInvoiceLineModal } from "@/components/invoices/AddInvoiceLineModal";
import { InvoiceBillingAddressFields } from "@/components/invoices/InvoiceBillingAddressFields";
import { InvoiceDraftPdfPreview } from "@/components/invoices/InvoiceDraftPdfPreview";
import { InvoiceLinesEditor } from "@/components/invoices/InvoiceLinesEditor";
import { InvoicePaymentsSection } from "@/components/invoices/InvoicePaymentsSection";
import { InvoiceQuickPaymentPanel } from "@/components/invoices/InvoiceQuickPaymentPanel";
import { InvoiceTypeBadge } from "@/components/invoices/InvoiceTypeBadge";
import { canAddInvoiceLine } from "@/lib/invoiceLineEdit";
import {
  billingAddressToPayload,
  guestToBillingAddressForm,
  invoiceToBillingAddressForm,
  validateBillingAddressForm,
  type BillingAddressFormState,
} from "@/lib/invoiceBillingAddress";
import { canShowInvoicePdf, fetchInvoicePdf } from "@/lib/invoicePdf";
import { formatCurrency } from "@/lib/money";
import { canWriteInvoice } from "@/lib/permissions";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { Booking, Guest, Invoice } from "@/types/camping";

function invoicePreviewRevision(invoice: Invoice): string {
  const lines = invoice.lines
    .map(
      (line) =>
        `${line.id}:${line.description}:${line.quantity}:${line.unit_price}:${line.line_total}`,
    )
    .join("|");
  return [
    invoice.status,
    invoice.total_amount,
    invoice.tax_amount,
    invoice.notes ?? "",
    invoice.billing_address_override ? "1" : "0",
    invoice.billing_company_name ?? "",
    invoice.billing_name ?? "",
    invoice.billing_street ?? "",
    invoice.billing_postal_code ?? "",
    invoice.billing_city ?? "",
    invoice.billing_country_code ?? "",
    lines,
  ].join("~");
}

export interface OpenedInvoiceDraftModalProps {
  invoiceId: string | null;
  open: boolean;
  onClose: () => void;
  userGroups: string[];
  onChanged?: () => void;
}

export function OpenedInvoiceDraftModal({
  invoiceId,
  open,
  onClose,
  userGroups,
  onChanged,
}: OpenedInvoiceDraftModalProps) {
  const canInvoice = canWriteInvoice(userGroups);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [billingAddress, setBillingAddress] = useState<BillingAddressFormState>({
    override: false,
    company_name: "",
    name: "",
    street: "",
    postal_code: "",
    city: "",
    country_code: "DE",
  });
  const [billingDirty, setBillingDirty] = useState(false);
  const [guestEmail, setGuestEmail] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!invoiceId || !open) return;
    setLoading(true);
    setError(null);
    api
      .get<Invoice>(`/api/v1/invoices/${invoiceId}`)
      .then((inv) => {
        setInvoice(inv);
        setBillingAddress(invoiceToBillingAddressForm(inv));
        setBillingDirty(false);
        if (!inv.billing_address_override) {
          api
            .get<Guest>(`/api/v1/guests/${inv.guest_id}`)
            .then((guest) => {
              setBillingAddress(guestToBillingAddressForm(guest));
              setGuestEmail(guest.email ?? null);
            })
            .catch(() => undefined);
        } else {
          api
            .get<Guest>(`/api/v1/guests/${inv.guest_id}`)
            .then((guest) => setGuestEmail(guest.email ?? null))
            .catch(() => setGuestEmail(null));
        }
        if (inv.booking_id) {
          api
            .get<Booking>(`/api/v1/bookings/${inv.booking_id}`)
            .then(setBooking)
            .catch(() => setBooking(null));
        } else {
          setBooking(null);
        }
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [invoiceId, open]);

  useEffect(() => {
    if (open && invoiceId) {
      load();
    } else {
      setInvoice(null);
      setAddLineOpen(false);
    }
  }, [open, invoiceId, load]);

  async function saveBillingAddress() {
    if (!invoice) return;
    const billingError = validateBillingAddressForm(billingAddress);
    if (billingError) {
      setError(billingError);
      return;
    }
    setAction("billing");
    setError(null);
    try {
      const payload = billingAddressToPayload(billingAddress);
      const updated = await api.patch<Invoice>(
        `/api/v1/invoices/${invoice.id}/billing-address`,
        payload,
      );
      setInvoice(updated);
      setBillingAddress(invoiceToBillingAddressForm(updated));
      setBillingDirty(false);
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnungsanschrift konnte nicht gespeichert werden.");
    } finally {
      setAction(null);
    }
  }

  async function ensureBillingSaved() {
    if (!invoice || !billingDirty) return;
    const billingError = validateBillingAddressForm(billingAddress);
    if (billingError) {
      throw new Error(billingError);
    }
    const payload = billingAddressToPayload(billingAddress);
    const updated = await api.patch<Invoice>(
      `/api/v1/invoices/${invoice.id}/billing-address`,
      payload,
    );
    setInvoice(updated);
    setBillingAddress(invoiceToBillingAddressForm(updated));
    setBillingDirty(false);
  }

  async function issueInvoice() {
    if (!invoice) return;
    setAction("issue");
    setError(null);
    try {
      if (billingDirty) {
        const billingError = validateBillingAddressForm(billingAddress);
        if (billingError) {
          setError(billingError);
          return;
        }
        const payload = billingAddressToPayload(billingAddress);
        await api.patch<Invoice>(`/api/v1/invoices/${invoice.id}/billing-address`, payload);
        setBillingDirty(false);
      }
      await api.post<Invoice>(`/api/v1/invoices/${invoice.id}/issue`, {});
      load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnung konnte nicht ausgestellt werden.");
    } finally {
      setAction(null);
    }
  }

  async function openPdf(mode: "preview" | "download") {
    if (!invoice) return;
    setAction(`pdf-${mode}`);
    setError(null);
    try {
      await fetchInvoicePdf(invoice.id, invoice.invoice_number, mode);
    } catch (e) {
      setError(e instanceof Error ? e.message : "PDF konnte nicht geladen werden.");
    } finally {
      setAction(null);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rechnungsentwurf"
      description={
        invoice
          ? `${invoice.invoice_number} – Positionen prüfen und ggf. ergänzen`
          : "Rechnung wird geladen…"
      }
      size="xl"
    >
      {invoice ? (
        <AddInvoiceLineModal
          open={addLineOpen}
          invoice={invoice}
          onClose={() => setAddLineOpen(false)}
          onAdded={() => {
            load();
            onChanged?.();
          }}
        />
      ) : null}

      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-label-secondary">Lade Rechnung…</p>
        ) : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        {invoice ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <InvoiceTypeBadge invoiceType={invoice.invoice_type} />
              <StatusBadge status={invoice.status} kind="invoice" />
              <span className="tabular-nums text-sm font-medium text-label">
                {formatCurrency(invoice.total_amount, invoice.currency)}
              </span>
            </div>

            {invoice.status === "draft" ? (
              <InvoiceDraftPdfPreview
                invoiceId={invoice.id}
                revision={invoicePreviewRevision(invoice)}
                staleHint={
                  billingDirty
                    ? "Die geänderte Rechnungsanschrift erscheint in der Vorschau, sobald sie gespeichert ist."
                    : null
                }
              />
            ) : null}

            <InvoiceLinesEditor
              invoice={invoice}
              booking={booking}
              disabled={!!action}
              onChanged={() => {
                load();
                onChanged?.();
              }}
            />

            {canInvoice && invoice.status === "draft" ? (
              <div className="space-y-2">
                <InvoiceBillingAddressFields
                  value={billingAddress}
                  onChange={(next) => {
                    setBillingAddress(next);
                    setBillingDirty(true);
                  }}
                  disabled={!!action}
                />
                {billingDirty ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={action === "billing"}
                    disabled={!!action}
                    onClick={() => void saveBillingAddress()}
                  >
                    Rechnungsanschrift speichern
                  </Button>
                ) : null}
              </div>
            ) : null}

            {canInvoice && canAddInvoiceLine(invoice) ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!!action}
                onClick={() => setAddLineOpen(true)}
              >
                Position hinzufügen
              </Button>
            ) : null}

            {canShowInvoicePdf(invoice.status) ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={action === "pdf-preview"}
                  disabled={!!action}
                  onClick={() => void openPdf("preview")}
                >
                  PDF anzeigen
                </Button>
              </div>
            ) : null}

            {canInvoice && invoice.status === "draft" ? (
              <div className="space-y-2 border-t border-separator pt-3">
                <p className="text-xs text-label-secondary">
                  Beim Ausstellen wird die Rechnung als unveränderbares PDF gespeichert.
                </p>
                <Button
                  type="button"
                  loading={action === "issue"}
                  disabled={!!action}
                  onClick={() => void issueInvoice()}
                >
                  Rechnung fertigstellen & ausstellen
                </Button>
              </div>
            ) : null}

            <InvoiceQuickPaymentPanel
              invoice={invoice}
              guestEmail={guestEmail}
              canRecord={canInvoice}
              disabled={!!action}
              onBeforeCollect={ensureBillingSaved}
              onPaymentComplete={() => {
                load();
                onChanged?.();
              }}
            />

            <InvoicePaymentsSection
              invoice={invoice}
              canRecord={canInvoice}
              onPaymentRecorded={() => {
                load();
                onChanged?.();
              }}
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
}
